import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Facebook as FacebookIcon,
  MusicNote as TikTokIcon,
  CameraAlt as SnapchatIcon,
  People as PeopleIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useAuth } from '../hooks/useAuth';
import { userAPI, socialAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConnectMoreModal from '../components/common/ConnectMoreModal';

const Dashboard = () => {
  const { user } = useAuth();
  const [connectModalOpen, setConnectModalOpen] = useState(false);

  useEffect(() => {
    // Handle OAuth callback on dashboard
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const refresh = urlParams.get('refresh');

    if (token && refresh) {
      console.log('OAuth callback detected on dashboard');
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refresh);
      // Clean up URL and reload to trigger auth state update
      window.history.replaceState({}, document.title, window.location.pathname);
      // Use a small delay to ensure tokens are saved before reload
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }, []);

  const { data: dashboardData, isLoading: dashboardLoading, error: dashboardError } = useQuery(
    'dashboard',
    userAPI.getDashboard,
    { 
      refetchInterval: 30000,
      retry: false,
      onError: (error) => {
        console.error('Dashboard data fetch failed:', error);
      },
      onSuccess: (data) => {
        console.log('Dashboard data fetched successfully:', data);
      }
    }
  );

  const { data: socialData, isLoading: socialLoading } = useQuery(
    'socialData',
    () => socialAPI.getAllData('7d'),
    { refetchInterval: 60000 }
  );

  // Fetch accounts directly as source of truth for the connected accounts panel
  const { data: accountsList, isLoading: accountsLoading } = useQuery(
    'accountsList',
    socialAPI.getAccounts,
    { refetchInterval: 60000 }
  );

  const getPlatformIcon = (platform) => {
    const icons = {
      instagram: null,
      youtube: null,
      facebook: null,
      tiktok: null,
      snapchat: null
    };
    return icons[platform] || null;
  };

  const getPlatformColor = () => '#1976d2';

  // Debug logging
  console.log('Dashboard - user:', user);
  console.log('Dashboard - dashboardData:', dashboardData);
  console.log('Dashboard - dashboardError:', dashboardError);
  console.log('Dashboard - dashboardLoading:', dashboardLoading);
  
  // Additional debugging for connected accounts (after response normalization)
  if (dashboardData) {
    console.log('Dashboard - Platforms array:', dashboardData.platforms);
    console.log('Dashboard - Platforms length:', dashboardData.platforms?.length);
    console.log('Dashboard - Full data structure:', JSON.stringify(dashboardData, null, 2));
    
    const youtubeAccounts = dashboardData.platforms?.filter(acc => acc.platform === 'youtube');
    console.log('Dashboard - YouTube accounts found:', youtubeAccounts?.length || 0);
    console.log('Dashboard - YouTube accounts:', youtubeAccounts);
  }

  if (dashboardLoading || socialLoading || accountsLoading) {
    return <LoadingSpinner message="Loading your dashboard..." />;
  }

  // Get connected accounts from dashboard data (basic info)
  const connectedAccounts = (accountsList || dashboardData?.platforms || dashboardData?.data?.platforms) || [];
  // Get detailed social data for analytics
  const aggregatedData = socialData?.data || {};
  
  console.log('Dashboard - connectedAccounts:', connectedAccounts);
  console.log('Dashboard - connectedAccounts length:', connectedAccounts.length);
  console.log('Dashboard - connectedAccounts[0]:', connectedAccounts[0]);
  console.log('Dashboard - aggregatedData:', aggregatedData);
  console.log('Dashboard - connectedAccounts type:', typeof connectedAccounts);
  console.log('Dashboard - connectedAccounts isArray:', Array.isArray(connectedAccounts));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome back, {user?.display_name || 'User'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's an overview of your social media performance
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="h6">Total Followers</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {aggregatedData.total_followers?.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="h6">Total Posts</Typography>
              </Box>
              <Typography variant="h4" color="secondary">
                {aggregatedData.total_posts?.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="h6">Total Engagement</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {aggregatedData.total_engagement?.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="h6">Connected Accounts</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {connectedAccounts.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Connected Accounts */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">Connected Accounts</Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setConnectModalOpen(true)}
          >
            Connect More
          </Button>
        </Box>

        {connectedAccounts.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No accounts connected yet
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Connect your first social media account to start tracking your performance
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setConnectModalOpen(true)}
            >
              Connect Account
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {connectedAccounts.map((account, index) => {
              console.log(`Rendering account ${index}:`, account);
              return (
                <Grid item xs={12} sm={6} md={4} key={account.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Box>
                          <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                            {account.platform}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            @{account.username}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label={account.is_active ? 'Active' : 'Inactive'}
                        color={account.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>

      {/* Platform Performance */}
      {Object.keys(aggregatedData.platforms || {}).length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Platform Performance (Last 7 Days)
          </Typography>
          <Grid container spacing={3}>
            {Object.entries(aggregatedData.platforms).map(([platform, data]) => (
              <Grid item xs={12} md={6} key={platform}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                        {platform}
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Followers
                        </Typography>
                        <Typography variant="h6">
                          {data.followers?.toLocaleString() || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Posts
                        </Typography>
                        <Typography variant="h6">
                          {data.posts_count || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Engagement
                        </Typography>
                        <Typography variant="h6">
                          {data.total_engagement?.toLocaleString() || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Engagement Rate
                        </Typography>
                        <Typography variant="h6">
                          {data.engagement_rate?.toFixed(2) || 0}%
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Connect More Modal */}
      <ConnectMoreModal 
        open={connectModalOpen} 
        onClose={() => setConnectModalOpen(false)}
        connectedAccounts={connectedAccounts}
      />
    </Container>
  );
};

export default Dashboard;
